import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';

import { Chip } from 'components/Chip';
import { Grid } from 'components/Grid';
import { Image } from 'components/Image';
import { Link } from 'components/Link';
import { Text } from 'components/Text';
import { cx } from 'utility';
import * as styles from './ProductList.module.scss';

const propTypes = {
    className: PropTypes.string,
    items: PropTypes.array,
};

const defaultProps = {
    className: '',
    items: [],
};

function getClasses({ className }) {
    const classes = cx(
        className,
        styles['productList'],
    );

    return classes || undefined;
}

function ProductList({
    className,
    items,
}) {
    const [containerOffset, setContainerOffset] = useState(0);
    const containerRef = useRef(null)
    const classes = getClasses({ className });

    function updateContanierOffset() {
        setContainerOffset((window.innerWidth - containerRef.current.offsetWidth) / 2);
    }

    useEffect(() => {
        updateContanierOffset();
        window.addEventListener('resize', updateContanierOffset);
        return () => window.removeEventListener('resize', updateContanierOffset);
    }, []);

    return (
        <div className={classes} style={{'--container-offset': `${containerOffset}px`}}>
            <div className={styles['container']}>
                <InView threshold={0.1} triggerOnce={true}>
                    {({ inView, ref, entry }) => {
                        return (
                            <div className={cx(styles['wrapper'], (inView) && styles['inview'])} ref={ref}>
                                {items.map(({ product_brand, product_image, product_label, product_name, product_review, product_url }, index) => {
                                    const brand = product_brand;
                                    const image = product_image;
                                    const label = product_label;
                                    const name = product_name;
                                    const review = product_review;
                                    const url = product_url;
                                    const highlightClassName = (review?.uid || review?.url) ? styles['highlight'] : '';

                                    return (
                                        <div key={`product-item-${index}`} className={cx(styles['item'], highlightClassName)}>
                                            <div className={cx(styles['itemWrapper'], (url?.uid || url?.url) ? styles['hasLink'] : '')}>
                                                {image?.url && (
                                                    <div className={styles['mediaContainer']}>
                                                        {(!review?.uid && !review?.url) && (
                                                            <Image
                                                                className={styles['image']}
                                                                image={image}
                                                                size={192}
                                                            />
                                                        )}
                                                        {(review?.uid || review?.url) && (
                                                            <Link className={styles['imageLink']} to={review}>
                                                                <Image
                                                                    className={styles['image']}
                                                                    image={image}
                                                                />
                                                            </Link>
                                                        )}
                                                    </div>
                                                )}
                                                {label && (
                                                    <Chip className={styles['label']} color="fill">{label}</Chip>
                                                )}
                                                {name && (
                                                    <Text
                                                        as="strong"
                                                        className={styles['name']}
                                                        emphasis="medium"
                                                        size={2}
                                                    >
                                                        {(!url?.uid && !url?.url) && (
                                                            name
                                                        )}
                                                        {(url?.uid || url?.url) && (
                                                            <Link className={styles['nameLink']} emphasis="medium" size={2} to={url}>{name}</Link>
                                                        )}
                                                    </Text>
                                                )}
                                                {brand && (
                                                    <Text
                                                        as="span"
                                                        className={styles['brand']}
                                                        emphasis="low"
                                                        size={0}
                                                    >
                                                        {brand}
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                                
                                    );
                                })}
                            </div>
                        );
                    }}
                </InView>
            </div>
            <Grid>
                <div className={styles['containerRef']} ref={containerRef}></div>
            </Grid>
        </div>
    );
}

ProductList.propTypes = propTypes;
ProductList.defaultProps = defaultProps;

export { ProductList };
